/* eslint-disable camelcase */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Heading,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Bank } from '@finley-technologies/finley-types';
import {
  orderLoanAccountsLoans,
  ValleyBankArrangementGroup,
  parse,
  ValleyBankCustomer,
  generateReceivableStats,
} from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankArrangementGroupLazyQueryGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import {
  Alert,
  ProgressAlert,
} from 'packages/common-frontend/components/Alert';
import { CsvXlsxDownloadSelect } from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from 'packages/common-frontend/components/icons';
import { InfoCard } from 'packages/common-frontend/components/InfoCard';
import { IconTag } from 'packages/common-frontend/components/Tag';
import {
  dashboardRoutePadding,
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';
import {
  ArrangementGroupTag,
  useArrangmentGroupUrlParam,
} from 'valleyBank/domains/ArrangementGroups/hooks/ArrangementGroupsContext';
import { ArrangementGroupSearch } from 'valleyBank/domains/ArrangementGroups/ui/ArrangementGroupSearch';
import {
  CollateralAccountArrangementGroupNumbers,
  getArrangementGroupNumbers,
} from 'valleyBank/domains/Collateral/ui/CollateralAccountArrangementGroupNumbers';
import { CollateralAccountLoanAccountNumbers } from 'valleyBank/domains/Collateral/ui/CollateralAccountLoanAccountNumbers';
import { CustomerSummaryLink } from 'valleyBank/domains/Customers/hooks/CustomerContext';
import { LoanAccountTag } from 'valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { LoansTable } from 'valleyBank/domains/LoanAccounts/ui/LoansTable';
import { useDataMetadataContext } from 'valleyBank/domains/page/hooks/DataMetadataContext';

import 'source/borrower/borrower.scss';

const collateralAccountsDataKeyToHeaderMap = {
  accountNumber: 'Account Number',
  accountName: 'Account Name',
  productName: 'Product Name',
  loanAccountNumbers: 'Loan Accounts',
  arrangementGroupNumbers: 'Arrangement Group',
  collateralType: 'Collateral Type',
  cifNum: 'CIF Number',
  customerName: 'Customer',
  marketValue: 'Market Value',
  effectiveAdvanceRate: 'Blended Advance Rate',
  preAllocationLendableValue: 'Lendable Value',
};

const collateralAccountsHeaders = _.map(
  collateralAccountsDataKeyToHeaderMap,
  (header, key) => ({ label: header, key }),
);

const getCustomerName = (customers: ValleyBankCustomer[], cifnum?: string) => {
  if (!cifnum) return cifnum;
  const customer = customers.find((_customer) => _customer.cif_num === cifnum);
  return customer?.full_name ?? cifnum;
};

export const ArrangementGroupsOverviewRoute = () => {
  const toast = useToast();
  const history = useHistory();
  const { reportingDate, isPreview } = useDataMetadataContext();
  const { arrangementGroupNumber } = useArrangmentGroupUrlParam();

  const [
    arrangementGroup,
    setArrangementGroup,
  ] = useState<ValleyBankArrangementGroup | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notFound, setNotFound] = useState<boolean>(false);

  const [
    getArrangementGroup,
  ] = useGetValleyBankArrangementGroupLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      try {
        if (res.valleyBankArrangementGroup === null) {
          setNotFound(true);
        }
        if (res?.valleyBankArrangementGroup) {
          const newArrangementGroup = parse.parseValleyBankArrangementGroup(
            res.valleyBankArrangementGroup,
          );
          if (newArrangementGroup) {
            // @jendevelops TODO add as of date when fetch arrangement group
            setArrangementGroup(newArrangementGroup);
          }
        }
      } catch (err) {
        Sentry.captureException(err);
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (
      arrangementGroupNumber &&
      arrangementGroupNumber !== arrangementGroup?.arrangementGroupNumber
    ) {
      setNotFound(false);
      setIsLoading(true);
      setArrangementGroup(null);
      getArrangementGroup({
        variables: {
          arrangementGroupNumber,
          isPreview,
          reportingDate,
        },
      });
    }
  }, [
    getArrangementGroup,
    isPreview,
    arrangementGroup,
    arrangementGroupNumber,
    reportingDate,
  ]);

  const collateralAccounts = arrangementGroup?.collateralAccounts || [];

  const receivables =
    arrangementGroup?.collateralAccounts.flatMap(
      ({ receivables: _receivables }) => _receivables,
    ) || [];
  const [assignedReceivables, unassignedReceivables] = _.partition(
    receivables,
    (receivable) => Boolean(receivable.loan_account_number),
  );
  const customers = arrangementGroup?.customers || [];

  const loanParties =
    arrangementGroup?.loanAccounts.flatMap(
      ({ loanParties: _loanParties, asOfDate }) =>
        _loanParties.map((loanParty) => ({ ...loanParty, asOfDate })),
    ) || [];

  const allCustomers = _.uniqBy(_.concat(customers, loanParties), 'cif_num');

  const {
    totalPreAllocationLendableValue: unassignedTotalPreAllocationLendableValue,
    totalMarketValue: unassignedTotalMarketValue,
  } = generateReceivableStats(unassignedReceivables);

  const {
    totalPostAllocationLendableValue: assignedTotalPostAllocationLendableValue,
    totalMarketValue: assignedTotalMarketValue,
  } = generateReceivableStats(assignedReceivables);

  const collateralAccountsTableData = _.map(collateralAccounts, (account) => {
    const {
      accountNumber,
      productName,
      marketValue,
      effectiveAdvanceRate,
      preAllocationLendableValue,
      receivables: collateralAccountReceivables,
      cifNum,
      owner,
    } = account;

    const loanAccountNumbersStr =
      collateralAccountReceivables[0]?.receivableReponseTapeRow.baseFields
        .finley_assigned_to_loan_accounts;

    const loanAccountNumbers =
      typeof loanAccountNumbersStr === 'string'
        ? loanAccountNumbersStr
        : 'unassigned';

    const arrangementGroupNumbersStr = getArrangementGroupNumbers(
      collateralAccountReceivables,
    );
    const arrangementGroupNumbers =
      arrangementGroupNumbersStr.length === 0 && arrangementGroup
        ? arrangementGroup
        : arrangementGroupNumbersStr.join('|');

    return {
      accountNumber,
      accountName: '', // todo: add account name
      productName,
      loanAccountNumbers,
      arrangementGroupNumbers,
      collateralType: '', // todo: add collateral type
      cifNum,
      customer: owner?.full_name || getCustomerName(allCustomers, cifNum),
      marketValue,
      effectiveAdvanceRate,
      preAllocationLendableValue,
    };
  });

  let routeLevelError = false;
  return (
    <Box padding={dashboardRoutePadding}>
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load arrangement group page`;
            routeLevelError = true;
            toast(errorToastConfig);
            history.push('/');
          }
        }}
      >
        <VStack w="100%" spacing="24px" alignItems="flex-start">
          <Heading as="h1" fontSize="4xl" mb="60px">
            Arrangement Groups
          </Heading>
          <ArrangementGroupSearch />
          {isLoading && (
            <ProgressAlert>Loading arrangement group</ProgressAlert>
          )}
          {!arrangementGroup && notFound && (
            <Alert status="warning">
              <Text>Arrangement group not found</Text>
            </Alert>
          )}
          {!isLoading && arrangementGroupNumber && arrangementGroup && (
            <>
              <ArrangementGroupTag arrangmentGroup={arrangementGroupNumber} />

              <Heading as="h2" fontSize="24px" mb="24px">
                Customers
              </Heading>
              {arrangementGroup.customers.length === 0 && (
                <Alert status="info">
                  <Text>No customers associated with arrangement group</Text>
                </Alert>
              )}
              {arrangementGroup.customers.length > 0 && (
                <TableContainer w="100%">
                  <Table w="100%">
                    <Thead>
                      <Tr>
                        <Th>Customer CIF Number</Th>
                        <Th>Customer</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {arrangementGroup.customers.map(
                        ({ cif_num, full_name }) => (
                          <Tr key={cif_num}>
                            <Td>
                              <CustomerSummaryLink cifnum={cif_num}>
                                {cif_num}
                              </CustomerSummaryLink>
                            </Td>
                            <Td>
                              <CustomerSummaryLink cifnum={cif_num}>
                                {full_name}
                              </CustomerSummaryLink>
                            </Td>
                          </Tr>
                        ),
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
              <Heading as="h2" fontSize="24px" mb="24px">
                Loan Accounts
              </Heading>
              {arrangementGroup.loanAccounts.length === 0 && (
                <Alert status="info">
                  <Text>
                    No loan accounts associated with arrangement group
                  </Text>
                </Alert>
              )}
              {arrangementGroup.loanAccounts.length > 0 && (
                <Accordion
                  defaultIndex={[0]}
                  allowToggle
                  allowMultiple
                  w="100%"
                  border="1px"
                  borderRadius="8px"
                  borderColor={colors.gray[200]}
                >
                  {arrangementGroup.loanAccounts.map((loanAccount) => {
                    const {
                      loanAccountNumber,
                      principalBalance,
                      accountName,
                      arrangementGroupId,
                    } = loanAccount;
                    return (
                      <AccordionItem key={loanAccountNumber} p="24px">
                        <AccordionButton>
                          <HStack w="100%" justify="flex-start">
                            <Heading as="h3" fontSize="20px" fontWeight="bold">
                              {`${accountName || '-'}`}
                            </Heading>
                            <IconTag
                              icon="dollar"
                              text={`Loan Outstanding: ${
                                formatCurrencyAmount(
                                  principalBalance,
                                  Bank.Currency.USD,
                                ) || '-'
                              }`}
                            />
                          </HStack>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel mt="40px">
                          <HStack
                            w="100%"
                            justify="flex-start"
                            spacing="16px"
                            mb="32px"
                          >
                            <LoanAccountTag loanAccountId={loanAccountNumber} />
                            <ArrangementGroupTag
                              arrangmentGroup={arrangementGroupId}
                            />
                          </HStack>
                          <LoansTable
                            loans={orderLoanAccountsLoans([loanAccount])}
                            noLoansMessage="There are no loans associated with this arrangement group."
                            showStats={false}
                          />
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}

              <Heading as="h2" fontSize="24px" mb="24px">
                Collateral Accounts
              </Heading>
              {arrangementGroup.collateralAccounts.length === 0 && (
                <Alert status="info">
                  <Text>
                    No collateral accounts associated with arrangement group
                  </Text>
                </Alert>
              )}

              {arrangementGroup.collateralAccounts.length > 0 && (
                <>
                  <HStack spacing="24px" w="100%">
                    <InfoCard
                      title="Total Assigned Market Value"
                      value={formatCurrencyAmount(
                        assignedTotalMarketValue,
                        Bank.Currency.USD,
                      )}
                      icon={<Icon icon="dollar" size="24px" />}
                      flexGrow={1}
                    />
                    <InfoCard
                      title="Total Assigned Lendable Value"
                      value={formatCurrencyAmount(
                        assignedTotalPostAllocationLendableValue,
                        Bank.Currency.USD,
                      )}
                      icon={<Icon icon="dollar" size="24px" />}
                      flexGrow={1}
                    />
                    <InfoCard
                      title="Total Unassigned Market Value"
                      value={formatCurrencyAmount(
                        unassignedTotalMarketValue,
                        Bank.Currency.USD,
                      )}
                      icon={<Icon icon="dollar" size="24px" />}
                      flexGrow={1}
                    />
                    <InfoCard
                      title="Total Unassigned Lendable Value"
                      value={formatCurrencyAmount(
                        unassignedTotalPreAllocationLendableValue,
                        Bank.Currency.USD,
                      )}
                      icon={<Icon icon="dollar" size="24px" />}
                      flexGrow={1}
                    />
                  </HStack>
                  <CsvXlsxDownloadSelect
                    dataRows={collateralAccountsTableData}
                    headers={collateralAccountsHeaders}
                    filenamePrefix={`collateral-accounts-arrangement-group-${arrangementGroup.arrangementGroupNumber}`}
                  />
                  <TableContainer w="100%" overflowX="scroll">
                    <Table>
                      <Thead>
                        <Tr>
                          {_.map(
                            collateralAccountsDataKeyToHeaderMap,
                            (header) => (
                              <Th>{header}</Th>
                            ),
                          )}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {arrangementGroup.collateralAccounts.map(
                          ({
                            accountNumber,
                            productName,
                            cifNum,
                            owner,
                            marketValue,
                            effectiveAdvanceRate,
                            preAllocationLendableValue,
                            receivables: collateralAccountReceivables,
                          }) => {
                            return (
                              <Tr key={accountNumber}>
                                <Td>{accountNumber}</Td>
                                {/* @jendevelops TODO: add account name */}
                                <Td>-</Td>
                                <Td>{productName}</Td>
                                <Td>
                                  <CollateralAccountLoanAccountNumbers
                                    receivables={collateralAccountReceivables}
                                  />
                                </Td>
                                <Td>
                                  <CollateralAccountArrangementGroupNumbers
                                    receivables={collateralAccountReceivables}
                                  />
                                </Td>
                                {/* TODO add collateral type */}
                                <Td>
                                  {receivables[0]?.receivableReponseTapeRow
                                    .baseFields.collateral_account_type || '-'}
                                </Td>
                                <Td>
                                  <CustomerSummaryLink cifnum={cifNum}>
                                    {cifNum}
                                  </CustomerSummaryLink>
                                </Td>
                                <Td>
                                  <CustomerSummaryLink cifnum={cifNum}>
                                    {owner?.full_name ||
                                      getCustomerName(allCustomers, cifNum)}
                                  </CustomerSummaryLink>
                                </Td>
                                <Td>
                                  {formatCurrencyAmount(
                                    marketValue,
                                    Bank.Currency.USD,
                                  )}
                                </Td>
                                <Td>
                                  {percentageToPercentageLabel(
                                    effectiveAdvanceRate * 100,
                                  )}
                                </Td>
                                <Td>
                                  {formatCurrencyAmount(
                                    preAllocationLendableValue,
                                    Bank.Currency.USD,
                                  )}
                                </Td>
                              </Tr>
                            );
                          },
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </VStack>
      </Sentry.ErrorBoundary>
    </Box>
  );
};
